import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Box, Button, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Textarea, useDisclosure } from '@chakra-ui/react'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'
import { getProjectInstallerPOsByProjectId, getProjectShipToByProjectId, upsertProjectShipTo } from 'api'

export const ProjectDetailGenericShipToInfo = ({ project, ...rest }) => {
  const [shipToInfo, setShipToInfo] = useImmer({
    id: -1,
    company: '',
    contact: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    log: ''
  })
  const disclosure = useDisclosure()

  const load = async () => {
    if (!project?.id) return
    try {
      const data = await getProjectShipToByProjectId(project.id)
      setShipToInfo({
        id: data.id,
        company: data.company || '',
        contact: data.contact || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        log: data.log || ''
      })
    } catch (err) {
      setShipToInfo({
        id: -1,
        company: '',
        contact: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        log: ''
      })
    }
  }

  useEffect(() => {
    load()
  }, [project])

  return (
    <>
      <Box {...rest}>
        <Heading size="sm" mb={2}>Ship To Information</Heading>
        <FormControl isRequired>
          <FormLabel>Company</FormLabel>
          <Input type="text" name="company" value={shipToInfo.company} isReadOnly={true} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Contact</FormLabel>
          <Input type="text" name="contact" value={shipToInfo.contact} isReadOnly={true} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Address</FormLabel>
          <Input type="text" name="address" value={shipToInfo.address} isReadOnly={true} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>City</FormLabel>
          <Input type="text" name="city" value={shipToInfo.city} isReadOnly={true} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>State</FormLabel>
          <Input type="text" name="state" value={shipToInfo.state} isReadOnly={true} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Zip</FormLabel>
          <Input type="text" name="zip" value={shipToInfo.zip} isReadOnly={true} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Log</FormLabel>
          <Textarea name="log" rows={5} value={shipToInfo.log} isReadOnly={true} />
        </FormControl>
        <Flex justifyContent="flex-end" gap={4} mt={4}>
          <Button
            size="md"
            colorScheme="gray"
            onClick={disclosure.onOpen}
          >
            Edit
          </Button>
        </Flex>
      </Box>
      <ModalEditShipToInfo
        project={project}
        shipToInfo={shipToInfo}
        reload={load}
        {...disclosure}
      />
    </>
  )
}

ProjectDetailGenericShipToInfo.propTypes = {
  project: PropTypes.object
}

const ModalEditShipToInfo = ({ isOpen, onClose, project, shipToInfo, reload }) => {
  const { createToast } = useAppContext()
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingAddress, setIsLoadingAddress] = useState(false)
  const [form, setForm] = useImmer({
    company: '',
    contact: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    log: ''
  })

  useEffect(() => {
    setForm(draft => {
      draft.company = shipToInfo.company
      draft.contact = shipToInfo.contact
      draft.address = shipToInfo.address
      draft.city = shipToInfo.city
      draft.state = shipToInfo.state
      draft.zip = shipToInfo.zip
      draft.log = shipToInfo.log
    })
  }, [shipToInfo])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      await upsertProjectShipTo(shipToInfo.id, { ...form, projectId: project?.id })
      setIsSaving(false)
      createToast('success', 'Success', 'Ship to information updated.')
      reload()
      onClose()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSaving(false)
    }
  }

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const onClickUseClientAddress = () => {
    setForm(draft => {
      draft.company = project?.customerCompany || ''
      draft.contact = project?.customerPhone || ''
      draft.address = project?.customerAddress || ''
      draft.city = project?.customerCity || ''
      draft.state = project?.customerState || ''
      draft.zip = project?.customerZip || ''
    })
  }

  const onClickUseInstallerAddress = async () => {
    if (!project?.id) return

    setIsLoadingAddress(true)
    try {
      const installerPOs = await getProjectInstallerPOsByProjectId(project?.id)
      if (!installerPOs.length) {
        createToast('warning', 'Warning', 'You didn\'t assign installer on this project')
      } else {
        setForm(draft => {
          draft.company = installerPOs[0].company || ''
          draft.contact = installerPOs[0].phone || ''
          draft.address = installerPOs[0].address || ''
          draft.city = installerPOs[0].city || ''
          draft.state = installerPOs[0].state || ''
          draft.zip = installerPOs[0].zip || ''
        })
      }
      setIsLoadingAddress(false)
    } catch (err) {
      createToast('error', 'Error', 'Failed to load installer address. Confirm if you have created PO for installer.')
      setIsLoadingAddress(false)
    }
  }

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>Ship To Information</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Grid templateColumns="repeat(2, 1fr)" gap={8}>
              <GridItem colSpan={1}>
                <FormControl isRequired>
                  <FormLabel>Company</FormLabel>
                  <Input type="text" name="company" value={form.company} onChange={handleChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Contact</FormLabel>
                  <Input type="text" name="contact" value={form.contact} onChange={handleChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Address</FormLabel>
                  <Input type="text" name="address" value={form.address} onChange={handleChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>City</FormLabel>
                  <Input type="text" name="city" value={form.city} onChange={handleChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>State</FormLabel>
                  <Input type="text" name="state" value={form.state} onChange={handleChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Zip</FormLabel>
                  <Input type="text" name="zip" value={form.zip} onChange={handleChange} />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isRequired>
                  <FormLabel>Log</FormLabel>
                  <Textarea name="log" rows={10} value={form.log} isReadOnly={true} />
                </FormControl>
              </GridItem>
            </Grid>
            <Flex justifyContent="flex-end" gap={4} mt={4}>
              <Button
                size="md"
                colorScheme="gray"
                onClick={onClickUseClientAddress}
              >
                Use Client Address
              </Button>
              <Button
                size="md"
                colorScheme="gray"
                onClick={onClickUseInstallerAddress}
                isLoading={isLoadingAddress}
              >
                Use Installer Address
              </Button>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap={4}>
              <Button
                type="submit"
                size="md"
                colorScheme="blue"
                isLoading={isSaving}
              >
                Save
              </Button>
              <Button
                size="md"
                colorScheme="gray"
                onClick={onClose}
              >
                Close
              </Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

ModalEditShipToInfo.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  project: PropTypes.object,
  shipToInfo: PropTypes.object,
  reload: PropTypes.func
}
